// src/App.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import Dashboard from "./pages/Dashboard";
import Contacts from "./pages/Contacts";
import Tasks from "./pages/Tasks";
import Settings from "./pages/Settings";
import TeamActivity from "./pages/TeamActivity";
import Deals from "./pages/Deals";
import Support from "./pages/Support";
import Analytics from "./pages/Analytics";
import Finances from "./pages/Finances";
import Engagement from "./pages/Engagement";
import Marketing from "./pages/Marketing";
import Revenue from "./pages/Revenue";
import Login from "./pages/Login";
import ContactPanel from "./components/ContactPanel";
import DealPanel from "./components/DealPanel";
import { auth } from "./lib/firebase";
import { displayEmailForAuthUser } from "./lib/crmUsers";
import { syncMarketingAttributionIfPresent } from "./lib/marketingAttribution";
import { watchContacts, watchTasks, watchDeals, watchScansCollection, getContact } from "./lib/db";
import { enrichContactsWithScanRollup } from "./lib/contactScanRollup";
import { ThemeProvider } from "./components/ThemeProvider";
import CommandPalette, { useCommandPalette } from "./components/CommandPalette";
import "./styles.css";

export default function App() {
  const [authUser, setAuthUser] = useState(() => auth.currentUser);
  const [authReady, setAuthReady] = useState(false);
  const [page, setPage] = useState("dashboard");
  const [selectedContact, setSelectedContact] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [dealPanelOpen, setDealPanelOpen] = useState(false);
  const [contactCount, setContactCount] = useState(0);
  const [openTaskCount, setOpenTaskCount] = useState(0);
  const [dealCount, setDealCount] = useState(0);
  const [contactsList, setContactsList] = useState([]);
  const [scansList, setScansList] = useState([]);
  const [tasksList, setTasksList] = useState([]);
  const [dealsList, setDealsList] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const [financesTab, setFinancesTab] = useState("overview");
  const { open: cmdkOpen, setOpen: setCmdkOpen } = useCommandPalette();

  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setAuthUser(u);
      setAuthReady(true);
      if (u) {
        syncMarketingAttributionIfPresent(u).catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    setNavOpen(false);
  }, [page]);

  useEffect(() => {
    const u1 = watchContacts((c) => {
      setContactCount(c.length);
      setContactsList(c);
    });
    const u2 = watchTasks((t) => {
      setTasksList(t);
      setOpenTaskCount(t.filter((x) => x.status === "open").length);
    });
    const u3 = watchDeals((d) => {
      setDealsList(d);
      setDealCount(d.filter((x) => x.stage !== "lost").length);
    });
    const u4 = watchScansCollection(setScansList, () => {});
    return () => {
      u1();
      u2();
      u3();
      u4();
    };
  }, []);

  const enrichedContactsList = useMemo(
    () => enrichContactsWithScanRollup(contactsList, scansList),
    [contactsList, scansList]
  );

  const newContactDraft = {
    isNew: true,
    name: "",
    email: "",
    phone: "",
    companyName: "",
    role: "",
    plan: "Free",
    stage: "lead",
    industry: "",
    stripeCustomerId: "",
    firebaseUid: "",
    mrr: 0,
    lifetimeRevenue: "",
    isInternal: false,
    healthScoreManual: false,
    healthScore: 50,
    totalScans: 0,
    notes: "",
    customFields: {},
  };

  function openContact(contact) {
    const enriched =
      enrichedContactsList.find((c) => c.id === contact?.id) ||
      enrichContactsWithScanRollup([contact], scansList)[0];
    setSelectedContact(enriched);
    setPanelOpen(true);
  }

  function openNewContact() {
    setSelectedContact({ ...newContactDraft });
    setPanelOpen(true);
  }

  function handleTopbarPageAction() {
    if (page === "contacts") openNewContact();
  }

  async function handleContactCreated(id) {
    const row = await getContact(id);
    if (row) setSelectedContact(row);
  }

  function closePanel() {
    setPanelOpen(false);
    setTimeout(() => setSelectedContact(null), 280);
  }

  function openDeal(id) {
    setSelectedDealId(id);
    setDealPanelOpen(true);
  }

  function closeDealPanel() {
    setDealPanelOpen(false);
    setTimeout(() => setSelectedDealId(null), 280);
  }

  const pages = {
    dashboard: (
      <Dashboard onOpenContact={openContact} onOpenTeamActivity={() => setPage("activity")} />
    ),
    contacts: <Contacts onOpenContact={openContact} onNewContact={openNewContact} />,
    tasks: <Tasks onOpenContact={openContact} />,
    activity: <TeamActivity onOpenContact={openContact} />,
    deals: (
      <Deals
        onOpenDeal={openDeal}
        contacts={enrichedContactsList}
        userEmail={displayEmailForAuthUser(authUser)}
      />
    ),
    support: <Support contacts={enrichedContactsList} />,
    analytics: (
      <Analytics
        onOpenContact={openContact}
        onGoFinances={(t) => {
          setFinancesTab(t || "overview");
          setPage("finances");
        }}
        onGoEngagement={() => setPage("engagement")}
      />
    ),
    finances: <Finances initialTab={financesTab} />,
    engagement: <Engagement onOpenContact={openContact} />,
    marketing: <Marketing contacts={enrichedContactsList} />,
    revenue: <Revenue onOpenContact={openContact} />,
    settings: <Settings />,
  };

  const pageTitles = {
    dashboard: "Dashboard",
    contacts: "Contacts",
    tasks: "Task inbox",
    activity: "Activity",
    deals: "Deals",
    support: "Support",
    analytics: "Analytics",
    finances: "Finances",
    engagement: "Engagement",
    marketing: "Marketing",
    revenue: "Revenue Ops",
    settings: "Settings",
  };

  if (!authReady) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-2)" }}>
        Loading…
      </div>
    );
  }

  if (!authUser) {
    return <Login />;
  }

  return (
    <ThemeProvider>
    <div className="app-shell">
      <Sidebar
        activePage={page}
        onNavigate={setPage}
        contactCount={contactCount}
        openTaskCount={openTaskCount}
        dealCount={dealCount}
        mobileOpen={navOpen}
        onCloseMobile={closeNav}
        userEmail={displayEmailForAuthUser(authUser)}
        onSignOut={() => signOut(auth)}
      />
      <div className="app-main">
        <Topbar
          title={pageTitles[page]}
          page={page}
          onOpenNav={() => setNavOpen(true)}
          onPageAction={handleTopbarPageAction}
          onOpenSearch={() => setCmdkOpen(true)}
          userEmail={displayEmailForAuthUser(authUser)}
          tasks={tasksList}
          onOpenTasks={() => setPage("tasks")}
        />
        <CommandPalette
          open={cmdkOpen}
          onClose={() => setCmdkOpen(false)}
          contacts={enrichedContactsList}
          deals={dealsList}
          tasks={tasksList}
          onNavigate={setPage}
          onOpenContact={openContact}
          onOpenDeal={openDeal}
        />
        <div className={`app-content${page === "deals" ? " app-content--deals" : ""}`}>
          {pages[page]}
        </div>
      </div>
      <ContactPanel
        contact={selectedContact}
        open={panelOpen}
        onClose={closePanel}
        onContactCreated={handleContactCreated}
      />
      <DealPanel
        dealId={selectedDealId}
        open={dealPanelOpen}
        onClose={closeDealPanel}
        contacts={enrichedContactsList}
        onOpenContact={(c) => {
          closeDealPanel();
          setTimeout(() => openContact(c), 300);
        }}
      />
    </div>
    </ThemeProvider>
  );
}
